import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Row, Col } from "antd";
import { IoCheckmarkDoneOutline } from "react-icons/io5";
import AOS from 'aos';

// Assets
import box2 from "../assets/icon/sidebar.png";
import box1 from "../assets/icon/about-icon.png";
import man from "../assets/About/menbook.png";
import blackshirt from "../assets/About/blackshirt.png";
import pinkshirt from "../assets/About/pinkshirt.png";
import circle from "../assets/About/circule.png";
import heart from "../assets/About/heart.png";

gsap.registerPlugin(ScrollTrigger);

function Aboutour() {
  const sectionRef = useRef(null);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
    });
  }, []);

  useEffect(() => {
    // Only run animations on desktop
    if (window.innerWidth >= 768) {
      let ctx = gsap.context(() => {
        // Animate text content
        gsap.from(".main-about", {
          opacity: 0,
          x: 80,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".main-about",
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });

        gsap.from(".main-about h1, .main-about p, .main-about ul li", {
          opacity: 0,
          y: 40,
          duration: 0.8,
          ease: "power2.out",
          stagger: 0.2,
          scrollTrigger: {
            trigger: ".main-about",
            start: "top 80%",
          },
        });

        // Animate hero images on scroll
        gsap.from(".hero-img", {
          opacity: 1,
          y: 80,
          scale: 1.5,
          duration: 1.2,
          ease: "power3.out",
          stagger: 0.3,
          scrollTrigger: {
            trigger: "#about-section",
            start: "top 20%",
          },
        });

        // Floating animation for hero images (infinite loop)
        gsap.utils.toArray(".float-item").forEach((el, i) => {
          gsap.to(el, {
            y: i % 2 === 0 ? 15 : -15,
            duration: 3 + i,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
          });
        });

        // Circle spins forever
        gsap.to(".circle-decor", {
          rotation: 360,
          duration: 12,
          repeat: -1,
          ease: "linear",
        });

        // Man image scales on scroll
        gsap.fromTo(
          ".man-img",
          { scale: 0 },
          {
            scale: 1.5,
            duration: 2,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".man-img",
              start: "top 80%",
              scrub: true,
            },
          }
        );
      }, sectionRef);

      return () => ctx.revert();
    }
  }, []);

  return (
    <div className="relative overflow-hidden py-8 md:py-12 lg:py-16">
      {/* Decorative box - Hidden on mobile */}
      <img 
        src={box2} 
        alt="decoration" 
        className="hero-img absolute -right-0 top-40 md:top-80 w-20 md:w-32 lg:w-40 hidden md:block opacity-50 md:opacity-100" 
      />
      
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <Row
          gutter={[24, 32]}
          align="middle"
          justify="center"
          id="about-section"
          ref={sectionRef}
        >
          {/* LEFT SIDE HERO IMAGES */}
          <Col xs={24} md={12} lg={12}>
            <section className="relative flex justify-center items-center min-h-[50vh] md:min-h-[70vh] lg:min-h-[90vh] bg-white">
              <div className="relative w-full max-w-[300px] md:max-w-[400px] lg:max-w-[500px] text-center">
                {/* Man image */}
                <img
                  src={man}
                  alt="Main hero"
                  className="hero-img float-item man-img relative z-10 w-full"
                  data-aos="zoom-in"
                />

                {/* Decorative shirts - Hidden on mobile */}
                <img
                  src={blackshirt}
                  alt="black shirt"
                  className="hero-img float-item absolute -left-12 md:-left-24 top-12 md:top-20 w-20 md:w-32 hidden sm:block"
                  data-aos="fade-right"
                />
                <img
                  src={pinkshirt}
                  alt="pink shirt"
                  className="hero-img float-item absolute -right-6 md:-right-10 top-8 md:top-12 w-20 md:w-32 hidden sm:block"
                  data-aos="fade-left"
                />
                <img
                  src={heart}
                  alt="heart"
                  className="hero-img absolute top-0 left-0 w-16 md:w-24 hidden md:block opacity-70"
                  data-aos="fade-down"
                />
                <img
                  src={circle}
                  alt="circle"
                  className="circle-decor absolute -right-4 md:-right-8 top-2 md:top-3 w-16 md:w-24 opacity-50 md:opacity-70 hidden sm:block"
                  data-aos="fade-up"
                />
              </div>

              {/* Customer badge */}
              <div className="absolute bottom-4 md:bottom-10 lg:bottom-20 left-1/2 -translate-x-1/2 border border-gray-200 bg-white rounded-lg shadow-md py-2 px-3 md:py-3 md:px-4">
                <p className="font-custom text-sm md:text-base lg:text-[20px] whitespace-nowrap">
                  <span className="text-base md:text-xl lg:text-[25px] font-custom text-[#95D8D1] font-semibold pr-2 italic">
                    25,000+
                  </span>
                  <span className="hidden sm:inline">Customer Worldwide</span>
                  <span className="sm:hidden">Customers</span>
                </p>
              </div>
            </section>
          </Col>

          {/* RIGHT SIDE TEXT CONTENT */}
          <Col xs={24} md={12} lg={12}>
            <div className="main-about">
              <p className="text-base md:text-lg lg:text-[25px] text-black mb-3 md:mb-4 font-custom">
                <span className="text-[#6cc5bf] text-2xl md:text-3xl lg:text-4xl">_</span> About Our Company
              </p>
              <h1 className="text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-black font-custom leading-tight md:leading-snug">
                High Quality{" "}
                <span className="text-[#6cc5bf] inline-block">Printing</span>
                {" "}Service In California
              </h1>
              <p className="font-outfit w-full md:w-[90%] lg:w-[80%] text-gray-700 mt-4 md:mt-5 text-sm md:text-base lg:text-lg font-light leading-relaxed">
                Your T-Shirt Is More Than Just Fabric — It's A Canvas For Your
                Ideas, Your Brand, And Your Story. At Initial Global Trade, We
                Make It Easy To Create Custom T-Shirts That Stand Out With Bold
                Colors, Durable Prints, And Endless Design Possibilities.
              </p>

              {/* Features */}
              <ul className="flex flex-col sm:flex-row items-start sm:items-center gap-4 md:gap-6 mt-5 md:mt-6">
                <li className="flex items-start gap-3 md:gap-5">
                  <img className="w-10 md:w-12 flex-shrink-0" src={box1} alt="icon" />
                  <div>
                    <h4 className="font-outfit text-base md:text-lg lg:text-xl font-semibold">
                      T-Shirt Printing
                    </h4>
                    <p className="font-custom text-xs md:text-sm text-gray-600">
                      Attract relevant users using specific hashtags.
                    </p>
                  </div>
                </li>

                <li className="flex items-start gap-3 md:gap-5">
                  <img className="w-10 md:w-12 flex-shrink-0" src={box1} alt="icon" />
                  <div>
                    <h4 className="font-outfit text-base md:text-lg lg:text-xl font-semibold">
                      Customized T-Shirt Printing
                    </h4>
                    <p className="font-custom text-xs md:text-sm text-gray-600">
                      Attract relevant users using specific hashtags.
                    </p>
                  </div>
                </li>
              </ul>

              {/* Checklist */}
              <div className="main-about-li mt-5 md:mt-6">
                <ul className="flex flex-col sm:flex-row items-start sm:items-center gap-4 md:gap-6 lg:gap-10">
                  <li className="flex items-start gap-2 font-custom text-sm md:text-base">
                    <span className="flex-shrink-0 mt-1">
                      <IoCheckmarkDoneOutline className="text-[#6cc5bf] text-lg md:text-xl" />
                    </span>
                    <p>Large print & stock selection & Unique Print</p>
                  </li>
                  <li className="flex items-start gap-2 font-custom text-sm md:text-base">
                    <span className="flex-shrink-0 mt-1">
                      <IoCheckmarkDoneOutline className="text-[#6cc5bf] text-lg md:text-xl" />
                    </span>
                    <p>Large print & stock selection & Unique Print</p>
                  </li>
                </ul>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default Aboutour;